import { LeaseRenewalTermPreference } from '@zg-rentals/ts-leasing-client';
import { FIELD_IDS, LeaseRenewalFormValues } from '../pages/TenantDrivenLeaseRenewal';
import { PROPOSED_RENT_AMOUNT_TYPE } from '../components/LeaseRenewal/Tenant/WhatDoYouWantToIncludeInYourRenewal';
import { formatCentsToCurrency } from './humanReadable';
import { Field } from '../models';

interface DefaultMessageTextParams {
  leaseRenewalFormData: LeaseRenewalFormValues;
  leaseInfo: {
    landlordFirstName: string;
    tenantFirstName: string;
  };
}

export function getDefaultMessageText({ leaseRenewalFormData, leaseInfo: leaseInfo }: DefaultMessageTextParams) {
  const {
    [FIELD_IDS.RENT_TYPE]: rentType,
    [FIELD_IDS.RENT_AMOUNT]: proposedRentAmount,
    [FIELD_IDS.RENEWAL_TYPE]: renewalType,
    [FIELD_IDS.RENEWAL_END_DATE]: renewalEndDate,
    [FIELD_IDS.WAIVE_LATE_FEES_CHECKED]: waiveLateFeesSelected,
    [FIELD_IDS.WAIVE_LATE_FEES]: gracePeriod,
    [FIELD_IDS.UPGRADE_APPLIANCE_CHECKED]: upgradeApplianceSelected,
    [FIELD_IDS.UPGRADE_APPLIANCE]: appliance,
    [FIELD_IDS.PROFESSIONAL_CLEANING_CHECKED]: professionalCleaningSelected,
    [FIELD_IDS.YARD_MAINTENANCE_CHECKED]: yardMaintenanceSelected,
    [FIELD_IDS.YARD_MAINTENANCE]: yardMaintenanceType,
    [FIELD_IDS.PAY_RENT_ON_TIME_CHECKED]: payRentOnTimeSelected,
    [FIELD_IDS.TAKES_CARE_OF_PROPERTY_CHECKED]: takeCareOfPropertySelected,
    [FIELD_IDS.RARELY_REQUESTS_REPAIRS_CHECKED]: rarelyRequestRepairsSelected,
    [FIELD_IDS.NO_NOISE_COMPLAINTS_CHECKED]: noNoiseComplaintsSelected,
  } = leaseRenewalFormData;
  const { landlordFirstName, tenantFirstName } = leaseInfo;

  let renewalLengthText = '';
  switch (renewalType) {
    case LeaseRenewalTermPreference.ONE_YEAR:
      renewalLengthText = 'for another year';
      break;
    case LeaseRenewalTermPreference.TWO_YEAR:
      renewalLengthText = 'for another two years';
      break;
    case LeaseRenewalTermPreference.MONTH_TO_MONTH:
      renewalLengthText = 'month to month';
      break;
    case LeaseRenewalTermPreference.CUSTOM:
      renewalLengthText = `until ${renewalEndDate}`;
      break;
  }

  let proposedRentText = '';
  const proposedRentAmountInCents = Field.getValueInCents(proposedRentAmount as string);
  const proposedRentAmountText = formatCentsToCurrency(Number(proposedRentAmountInCents));
  switch (rentType) {
    case PROPOSED_RENT_AMOUNT_TYPE.NO_PRICE_CHANGE:
      proposedRentText = `keeping the rent at ${proposedRentAmountText}`;
      break;
    case PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF:
      proposedRentText = '15% off the first month’s rent and no increase for the remaining months';
      break;
    case PROPOSED_RENT_AMOUNT_TYPE.CUSTOM:
      proposedRentText = `changing the rent to ${proposedRentAmountText}`;
      break;
  }

  const tenantDetails = [
    payRentOnTimeSelected && 'always paid rent on time',
    takeCareOfPropertySelected && 'take good care of the property',
    rarelyRequestRepairsSelected && 'rarely request repairs unless necessary',
    noNoiseComplaintsSelected && 'had no noise complaints or other issues with neighbors',
  ].filter(Boolean);

  let tenantDetailsText = 'I’ve been a responsible tenant';
  const tenantDetailsLength = tenantDetails.length;
  if (!tenantDetailsLength) {
    tenantDetailsText = '';
  } else if (tenantDetailsLength === 1) {
    tenantDetailsText = `${tenantDetailsText} and ${tenantDetails[0]}.`;
  } else {
    const lastTenantDetail = tenantDetails[tenantDetails.length - 1];
    tenantDetailsText += `, ${tenantDetails.slice(0, -1).join(', ')}, and ${lastTenantDetail}.`;
  }

  const additionalRequests = [
    waiveLateFeesSelected && `- Late fees are waived if rent is paid within ${gracePeriod} of the due date`,
    upgradeApplianceSelected && `- Landlord will upgrade the ${appliance.toLowerCase()}`,
    professionalCleaningSelected && `- Landlord will provide one professional cleaning to help maintain the rental`,
    yardMaintenanceSelected &&
      `- Landlord will provide yard maintenance, specifically ${yardMaintenanceType.toLowerCase()}`,
  ].filter(Boolean);

  const hasAdditionalRequests = additionalRequests.length > 0;

  const messageText = [
    landlordFirstName ? `Hi ${landlordFirstName},` : 'Hi,',
    '',
    `I’ve really enjoyed living in your rental and would like to discuss renewing my lease ${renewalLengthText}.${tenantDetails.length > 0 ? ' ' : ''}${tenantDetailsText}`,
    '',
    `For the renewal, would you consider ${proposedRentText}?`,
    '',
    ...(hasAdditionalRequests ? ['I would also appreciate adding the following to the lease renewal agreement:'] : []),
    ...additionalRequests,
    ...(hasAdditionalRequests ? [''] : []),
    'Thanks for your consideration!',
    tenantFirstName ? tenantFirstName : '',
  ].join('\n');

  return messageText;
}
